import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import { toast } from 'react-hot-toast';
import Header from 'shared/components/Header';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import { tokens } from 'shared/theme';
import { useAuth } from 'shared/hooks/useAuth';
import useIdleSessionRemaining from 'shared/hooks/useIdleSessionRemaining';
import useTenantsService from 'shared/api/services/tenantsService';

const DEFAULT_ROTATION_MONTHS = 4;
const DEFAULT_MAX_FAILED_ATTEMPTS = 4;

const cardSx = (colors) => ({
  flex: { xs: '1 1 100%', md: '1 1 360px' },
  maxWidth: { xs: '100%', md: 420 },
  backgroundColor: colors.primary[400],
});

const TenantSettingsPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { user } = useAuth();
  const { remainingMs, formatted, timeoutLabel } = useIdleSessionRemaining();
  const profile = user?.data;
  const tenantId =
    profile?.tenantId != null ? String(profile.tenantId).trim() : '';

  const { getTenantSettings, updateTenantSettings } = useTenantsService();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forcePasswordRotationEnabled, setForcePasswordRotationEnabled] = useState(false);
  const [lockAccountOnFailedLoginEnabled, setLockAccountOnFailedLoginEnabled] = useState(false);
  const [passwordRotationMonths] = useState(DEFAULT_ROTATION_MONTHS);
  const [maxFailedLoginAttempts] = useState(DEFAULT_MAX_FAILED_ATTEMPTS);

  const loadSettings = useCallback(async () => {
    if (!tenantId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await getTenantSettings(tenantId);
      setForcePasswordRotationEnabled(Boolean(data?.forcePasswordRotationEnabled));
      setLockAccountOnFailedLoginEnabled(Boolean(data?.lockAccountOnFailedLoginEnabled));
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to load settings';
      toast.error(typeof msg === 'string' ? msg : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [getTenantSettings, tenantId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async () => {
    if (!tenantId) {
      toast.error('No tenant is associated with your account.');
      return;
    }
    setSaving(true);
    try {
      await updateTenantSettings(tenantId, {
        forcePasswordRotationEnabled,
        passwordRotationMonths,
        lockAccountOnFailedLoginEnabled,
        maxFailedLoginAttempts,
      });
      toast.success('Settings saved.');
    } catch (error) {
      const msg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to save settings';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (!tenantId) {
    return (
      <Box m="20px">
        <Header title="Settings" />
        <Typography color="text.secondary" sx={{ mt: 2 }}>
          Settings are only available when you are signed in to a tenant workspace.
        </Typography>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Header title="Settings" />

      <Box
        sx={{
          mt: 2,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          flexWrap: 'wrap',
          gap: 2.5,
          alignItems: 'stretch',
        }}
      >
        <Card sx={cardSx(colors)}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Idle session time
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              You can stay idle for {timeoutLabel}. This number counts down only while you
              are not using the system. Navigating or clicking anywhere resets it to{' '}
              {timeoutLabel}.
            </Typography>
            <Typography
              component="div"
              sx={{
                fontSize: '2.25rem',
                fontWeight: 700,
                letterSpacing: 2,
                fontVariantNumeric: 'tabular-nums',
                color: remainingMs <= 60_000 ? colors.redAccent[400] : colors.greenAccent[400],
              }}
            >
              {formatted}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Remaining until sign-out if you stay idle
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          <Box
            sx={{
              mt: 2.5,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              flexWrap: 'wrap',
              gap: 2.5,
              alignItems: 'stretch',
            }}
          >
            <Card sx={cardSx(colors)}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Password policy
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  When enabled, users must change their password every{' '}
                  {passwordRotationMonths} months. They are prompted on their next login after
                  the period expires. This does not affect users until you turn it on.
                </Typography>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Switch
                      checked={forcePasswordRotationEnabled}
                      onChange={(e) => setForcePasswordRotationEnabled(e.target.checked)}
                      color="secondary"
                    />
                  }
                  label={`Require password change every ${passwordRotationMonths} months`}
                />
              </CardContent>
            </Card>

            <Card sx={cardSx(colors)}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Account lockout
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  When enabled, users are locked after {maxFailedLoginAttempts} failed login
                  attempts. Locked users cannot sign in until an administrator reactivates their
                  account. This does not affect users until you turn it on.
                </Typography>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Switch
                      checked={lockAccountOnFailedLoginEnabled}
                      onChange={(e) => setLockAccountOnFailedLoginEnabled(e.target.checked)}
                      color="secondary"
                    />
                  }
                  label={`Lock account after ${maxFailedLoginAttempts} failed login attempts`}
                />
              </CardContent>
            </Card>
          </Box>

          <Box mt={3}>
            <Button
              variant="contained"
              color="secondary"
              disabled={saving}
              onClick={handleSave}
            >
              {saving ? 'Saving…' : 'Save settings'}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default TenantSettingsPage;
